import { inject, Injectable } from '@angular/core';
import { CampaignsGQL, CampaignsQuery, CampaignByIdGQL, CampaignByIdQuery, LastCampaignGQL, LastCampaignQuery, DeleteCampaignLegalEntityGQL, DeleteCampaignLegalEntityMutation, CreateCampaignAndLegalEntitiesAssociationsGQL, CreateCampaignAndLegalEntitiesAssociationsMutation, OrderTypeInput, CampaignLegalEntityColumns, OperatorInput, ComparatorOperators, CloseCampaignMutation, CloseCampaignGQL, ValidateCampaignGQL, ValidateCampaignMutation, ApproveCampaignMutation, ApproveCampaignGQL, SubmittedForApprovalCampaignGQL, SubmittedForApprovalCampaignMutation, CampaignLegalEntityQueryComplexInput, CampaignLegalEntitySortInput, CampaignLegalEntitySimpleQueryInput, NotApproveCampaignMutation, NotApproveCampaignGQL, PagingInput, CampaignByIdWithoutKpiGQL, CampaignByIdWithoutKpiQuery, CreateSnapshotGQL, CreateNewCampaignGQL, CreateNewCampaignMutation } from '../../../graphql/generated';
import { map, Observable } from 'rxjs';
import { FilterResult, LazyLoadParameters } from '../features/administration/legal-entities/legal-entities.component';
import { LEGAL_FIELD } from '../shared/graphql-operations/legal-entities/legal.enum';
import { GraphFilter } from '../shared/graphql-operations/graph-ql.operations';

@Injectable({
  providedIn: 'root'
})
export class CampaignService {
  private readonly CampaignsGQL: CampaignsGQL = inject(CampaignsGQL);
  private readonly CampaignByIdGQL: CampaignByIdGQL = inject(CampaignByIdGQL);
  private readonly CampaignByIdWithoutKpiGQL: CampaignByIdWithoutKpiGQL = inject(CampaignByIdWithoutKpiGQL);
  private readonly LastCampaignGQL: LastCampaignGQL = inject(LastCampaignGQL);
  private readonly DeleteCampaignLegalEntityGQL: DeleteCampaignLegalEntityGQL = inject(DeleteCampaignLegalEntityGQL);
  private readonly CreateCampaignAndLegalEntitiesAssociationsGQL: CreateCampaignAndLegalEntitiesAssociationsGQL = inject(CreateCampaignAndLegalEntitiesAssociationsGQL);
  private readonly CloseCampaignGQL: CloseCampaignGQL = inject(CloseCampaignGQL);
  private readonly ValidateCampaignGQL: ValidateCampaignGQL = inject(ValidateCampaignGQL);
  private readonly ApproveCampaignGQL: ApproveCampaignGQL = inject(ApproveCampaignGQL);
  private readonly NotApproveCampaignGQL: NotApproveCampaignGQL = inject(NotApproveCampaignGQL);
  private readonly SubmitCampaignGQL: SubmittedForApprovalCampaignGQL = inject(SubmittedForApprovalCampaignGQL);
  private readonly CreateSnapshotGQL: CreateSnapshotGQL = inject(CreateSnapshotGQL);
  private readonly CreateNewCampaignGQL: CreateNewCampaignGQL = inject(CreateNewCampaignGQL);

  getCampaigns(): Observable<CampaignsQuery['campaigns']> {
    return this.CampaignsGQL.watch().valueChanges.pipe(map(result => result.data.campaigns));
  }
  getCampaignById(campaignId: string, parameters: LazyLoadParameters | undefined): Observable<CampaignByIdQuery['campaignLegalEntitiesDashboard']> {
    //TODO da aggiungere paramsComplex alle variables
    const param = convertLazyLoadToComplexWithoutPage(parameters);
    return this.CampaignByIdGQL.watch(
      {
        campaignId, query: param.query, sort: param.sort, page: param.page
      },
      {
        fetchPolicy: 'no-cache'
      }).valueChanges.pipe(map(result => result.data.campaignLegalEntitiesDashboard!));
  }

  createSnapshot(campaignId: string): Observable<string> {
    return this.CreateSnapshotGQL.mutate({ campaignId }).pipe(
      map(result => result.data?.createSnapshot ?? '')
    );
  }

  getCampaignByIdWithoutKpi(id: string): Observable<CampaignByIdWithoutKpiQuery['campaignLegalEntitiesByCampaignId']> {
    return this.CampaignByIdWithoutKpiGQL.watch(
      {
        id
      },
      {
        fetchPolicy: 'no-cache'
      }).valueChanges.pipe(map(result => result.data.campaignLegalEntitiesByCampaignId!));
  }

  getLastCampaign(params?: LazyLoadParameters | undefined): Observable<LastCampaignQuery> {
    return this.LastCampaignGQL.watch({
      query: [],
      sort: {
        sortField: LEGAL_FIELD[params?.order.sortField ?? 'id'],
        sortOrder: OrderTypeInput[OrderTypeInputInverted[params?.order.sortDirection ?? 'ASC']] ?? OrderTypeInput.Asc
      }
    }, {
      fetchPolicy: 'network-only'
    }).valueChanges.pipe(map(result => result.data));
  }
  deleteCampaignAndLegalEntitiesAssociations(ids: string[]): Observable<DeleteCampaignLegalEntityMutation["deleteCampaignAndLegalEntitiesAssociations"]> {
    return this.DeleteCampaignLegalEntityGQL.mutate({ ids }).pipe(map(result => result.data?.deleteCampaignAndLegalEntitiesAssociations))
  }
  createCampaignAndLegalEntitiesAssociations(ids: string[]): Observable<CreateCampaignAndLegalEntitiesAssociationsMutation["createCampaignAndLegalEntitiesAssociations"]> {
    return this.CreateCampaignAndLegalEntitiesAssociationsGQL.mutate({ ids }).pipe(map(result => result.data?.createCampaignAndLegalEntitiesAssociations))
  }

  createNewCampaign(): Observable<CreateNewCampaignMutation['createNewActiveCampaign']> {
    return this.CreateNewCampaignGQL.mutate({}, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createNewActiveCampaign))
  }

  closeCampaign(): Observable<CloseCampaignMutation['closeActiveCampaign']> {
    return this.CloseCampaignGQL.mutate().pipe(map(result => result.data?.closeActiveCampaign))
  }

  validateCampaign(
    campaignId: string,
    legalEntityIds: string[]
  ): Observable<ValidateCampaignMutation['validateCampaign']> {
    return this.ValidateCampaignGQL.mutate({
      campaignId,
      legalEntityIds
    }).pipe(map(result => result.data?.validateCampaign))
  }

  approveCampaign(
    campaignId: string,
    legalEntityIds: string[]
  ): Observable<ApproveCampaignMutation['approveCampaign']> {
    return this.ApproveCampaignGQL.mutate({
      campaignId,
      legalEntityIds
    }).pipe(map(result => result.data?.approveCampaign))
  }
  notApproveCampaign(
    campaignId: string,
    legalEntityIds: string[]
  ): Observable<NotApproveCampaignMutation['notApproveCampaign']> {
    return this.NotApproveCampaignGQL.mutate({
      campaignId,
      legalEntityIds
    }).pipe(map(result => result.data?.notApproveCampaign))
  }

  submitCampaign(
    campaignId: string,
    legalEntityIds: string[]
  ): Observable<SubmittedForApprovalCampaignMutation['submittedForApprovalCampaign']> {
    return this.SubmitCampaignGQL.mutate({
      campaignId,
      legalEntityIds
    }).pipe(map(result => result.data?.submittedForApprovalCampaign))
  }


}

enum OrderTypeInputInverted {
  ASC = 'Asc',
  DESC = 'Desc'
}

export function buildFilterSchema(filters: GraphFilter, fields_const: any) {
  const result: any[] = [];
  if (filters) {
    Object.entries(filters).map(
      (filter) => {
        if (filter[1] !== null ||
          ((filter[1] as any)?.valueArray !== null &&
            (filter[1] as any)?.valueArray?.length > 0)) {
          result.push(
            {
              field: fields_const[filter[0] === 'entityName' ? 'legalEntityName' : filter[0]],
              filters: {
                value: foundFilterValues(filter[1]),
                operator: foundFilterOperator(filter[1])
              }
            }
          )
        }
      }
    )
  }
  return result
}

export function foundFilterValues(
  filterValue: ((FilterResult & (string | number | Date)) | any)[]
) {
  return filterValue instanceof Array
    ? filterValue.map(f => f?.toString() ?? '')
    : (filterValue as any).valueArray?.toString() ?? '';
}

export function foundFilterOperator(filterValue: any) {
  return filterValue.operator ?? "MATCH";
}


export function convertLazyLoadToComplexWithoutPage(parameters: any): { query: CampaignLegalEntityQueryComplexInput, sort: CampaignLegalEntitySortInput, page: PagingInput } {
  const query: CampaignLegalEntitySimpleQueryInput[] = [];
  if (parameters) {

    if (parameters.filters) {

      Object.entries(parameters.filters).forEach(([field, values]) => {
        //console.log"value", values);

        if (values !== null && Array.isArray(values) && values.length > 0) {
          (ENUM_CPT[field as keyof typeof ENUM_CPT] || [field]).forEach(key => {
            query.push({
              field: key as CampaignLegalEntityColumns,
              filters: {
                operator: ComparatorOperators.Match,
                value: key === CampaignLegalEntityColumns.LegalEntityCode ? values.map(v => v?.split(/\s-\s(.+)/)[0]?.trim()) : values.map(v => v?.toLowerCase() === 'empty' ? null : v)
              }
            });
          })
        }
      });
    }
  }
  return {
    query: {
      operator: OperatorInput.And,
      query: [
        {
          query: query,
          operator: OperatorInput.And
        }
      ]
    },
    sort: {
      sortField: ENUM_CPT?.[parameters?.order?.sortField as keyof typeof ENUM_CPT]?.[0] as CampaignLegalEntityColumns || CampaignLegalEntityColumns.LegalEntityCode,
      // sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
      sortOrder: OrderTypeInput.Asc
    },
    page: {
      pageNo: parameters?.page ?? 0,
      pageSize: parameters?.rows ?? 1000000
    }
  }
}

export const ENUM_CPT = {
  businessUnit: [CampaignLegalEntityColumns.BusinessUnit],
  organizationalUnit: [CampaignLegalEntityColumns.OrganizationalUnit],
  entityName: [CampaignLegalEntityColumns.LegalEntityCode],
  legalEntityName: [CampaignLegalEntityColumns.LegalEntityCode],
  legalEntitySize: [CampaignLegalEntityColumns.LegalEntitySize],
  businessType: [CampaignLegalEntityColumns.BusinessType],
  valueChain: [CampaignLegalEntityColumns.ValueChain],
  category: [CampaignLegalEntityColumns.Category],
  auditControlApproach: [CampaignLegalEntityColumns.AuditControlApproach],
  startDate: [CampaignLegalEntityColumns.StartDate],
  riskAssessmentLastUpdate: [CampaignLegalEntityColumns.RiskAssessmentLastUpdate],
  riskAssessmentLastModified: [CampaignLegalEntityColumns.RiskAssessmentLastUpdate],
  validationStatus: [CampaignLegalEntityColumns.CampaignLegalEntityValidationStatus],
  lastValidation: [CampaignLegalEntityColumns.CampaignLegalEntityValidationDate],
  validatedBy: [CampaignLegalEntityColumns.CampaignLegalEntityValidatedBy]
}